import React, { FC } from 'react'
import styled from 'styled-components'

import { usePractitionerSettings } from '../../../../../../lib/practitioner-settings/hooks/usePractitionerSettings'
import {
  rebatesPayoutFeePercent,
  rebatesPayoutMinimum,
  rebatesPayoutPercent,
} from '../../../../../../lib/rewards/rebates'
import { dropZeroCents, formatAsCurrencyWithCommasOrZero } from '../../../../../../utils/priceTools'
import { RewardsInformationTooltip } from '../../RewardsInformationTooltip'
import { InformationIcon, NoticeCard, NoticeLink, NoticeText } from '../../RewardsNotice'
import {
  DetailsPayoutsCard,
  DetailsPayoutsCardActions,
  DetailsPayoutsCardAmount,
  DetailsPayoutsCardAmountLabel,
  DetailsPayoutsCardAmountSubtext,
  DetailsPayoutsCardContent,
  DetailsPayoutsCardHeading,
} from '../DetailsPayoutsCard'
import { RebatesRequestPayoutButton } from './RebatesRequestPayoutButton'

const NoticeCardContainer = styled.div`
  margin: 25px 0;
`

export interface RebatesCardProps {
  availableBalance: {
    rebateBalance: number
  }
  refetchBalance: () => Promise<void>
}

export const RebatesCard: FC<RebatesCardProps> = ({ availableBalance, refetchBalance }) => {
  const { rebateBalance } = availableBalance
  const rebateCashOutMinimumFormatted = dropZeroCents(
    formatAsCurrencyWithCommasOrZero({ amount: rebatesPayoutMinimum }),
  )

  const [settings] = usePractitionerSettings()
  const optedOutOfRebates = !(settings?.practitioner?.enrolledInRebate ?? true)
  const isEligibleForRebates = !settings?.practitioner?.ineligibleForRebate

  if (!isEligibleForRebates) {
    return null
  }

  return (
    <DetailsPayoutsCard>
      <DetailsPayoutsCardContent>
        <DetailsPayoutsCardHeading>Rebates</DetailsPayoutsCardHeading>
        {optedOutOfRebates ? (
          <NoticeCardContainer>
            <NoticeCard>
              <InformationIcon />
              <NoticeText>
                {`Currently not earning rebates. `}
                <NoticeLink to="/account/settings/rewards-program">
                  Enroll to rebates in Rewards Program
                </NoticeLink>
              </NoticeText>
            </NoticeCard>
          </NoticeCardContainer>
        ) : (
          <>
            <DetailsPayoutsCardAmountLabel>
              <DetailsPayoutsCardAmount>
                {dropZeroCents(formatAsCurrencyWithCommasOrZero({ amount: rebateBalance }))}
              </DetailsPayoutsCardAmount>
              available
            </DetailsPayoutsCardAmountLabel>
            <DetailsPayoutsCardAmountSubtext>
              minus {rebatesPayoutFeePercent}% cash out fee.
              <RewardsInformationTooltip>
                Once the rebate value exceeds {rebateCashOutMinimumFormatted}, rebates may be
                redeemed for a cash payment equal to the amount of {rebatesPayoutPercent}% of the
                outstanding rebate value.
              </RewardsInformationTooltip>
            </DetailsPayoutsCardAmountSubtext>
            <DetailsPayoutsCardAmountSubtext>
              {rebateCashOutMinimumFormatted} Minimum to cash out.
            </DetailsPayoutsCardAmountSubtext>
          </>
        )}
      </DetailsPayoutsCardContent>
      <DetailsPayoutsCardActions>
        <RebatesRequestPayoutButton
          availableBalance={availableBalance}
          refetchBalance={refetchBalance}
        />
      </DetailsPayoutsCardActions>
    </DetailsPayoutsCard>
  )
}
